import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { List, Map } from 'immutable';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { ISearchParams } from '../../../entity-data/entity-data.data';
import { IMasterListHeader } from '../../../entity-master/components/master-list/master-list.interface';
import { IMetadata, INoItemsOptions } from '../../../feed/feed.interface';
import { TrackedTime } from '../../../tracked-time-data/models/tracked-time.model';
import { TrackedTimeFiltersEnum } from '../../../tracked-time-data/tracked-time-data.data';
import { TrackedTimeDataDispatcher } from '../../../tracked-time-data/tracked-time-data.dispatcher';
import { TrackedTimeDataStore } from '../../../tracked-time-data/tracked-time-data.store';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITrackingObject,
  TrackingButtonIdentifierEnum,
  TrackingInputTypeValuesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackedTimeQuickActionList } from '../../tracked-time-quick-action-list';
import { TrackedTimeListBuilder } from './tracked-time-list.builder';
import { ISelectOption } from '../../../form-master/components/select-field/select-field.interface';
import { TrackedTimeFilters } from '../../../tracked-time-data/models/tracked-time-filters.model';
import {
  ALL_YEARS,
  DurationPerYearFilter,
} from '../../../tracked-time-data/models/duration-per-year-filter.model';
import { FriendlyDurationPipe } from '../../../shared/pipes/friendly-duration/friendly-duration.pipe';
import { filter, map, startWith, take, takeUntil } from 'rxjs/operators';
import { TrackedTimeMasterBuilder } from '../../tracked-time-master.builder';
import { IRouter } from '../../../shared/interfaces/router.interface';
import { Toggles } from '../../../features-data/sub-models/toggles.model';

@Component({
  selector: 'app-tracked-time-list',
  templateUrl: 'tracked-time-list.component.html',
  styleUrls: ['tracked-time-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [TrackedTimeListBuilder],
})
export class TrackedTimeListComponent
  extends TrackedTimeQuickActionList
  implements OnInit, OnDestroy {
  @Input() filter: TrackedTimeFiltersEnum;
  @Input() trackedTimes: List<TrackedTime>;
  @Input() noTrackedTimeDescription: string;

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _sortByHeader: BehaviorSubject<IMasterListHeader> = new BehaviorSubject<IMasterListHeader>(
    null
  );
  private _lastPageFetched: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  private _yearFilterOptions: BehaviorSubject<
    List<ISelectOption>
  > = new BehaviorSubject<List<ISelectOption>>(null);
  private _selectedYearFilter: BehaviorSubject<ISelectOption> = new BehaviorSubject<ISelectOption>(
    null
  );

  headers: List<IMasterListHeader>;
  noTrackedTimeOptions$: Observable<INoItemsOptions>;
  trackingObject: ITrackingObject;
  trackingScreenName: TrackingScreenNamesEnum;
  sortByHeader$: Observable<IMasterListHeader>;
  fetchingList$: Observable<boolean>;
  lastPageFetched$: Observable<boolean>;
  fetchingListFilters$: Observable<boolean>;
  yearFilterOptions$: Observable<List<ISelectOption>>;
  selectedYearFilter$: Observable<ISelectOption>;
  filterTotals$: Observable<string>;
  groupByFn$: Observable<Function>;
  groupMetadata$: Observable<Map<string, IMetadata>>;

  constructor(
    private _translateService: TranslateService,
    private _trackedTimeDataStore: TrackedTimeDataStore,
    private _trackedTimeListBuilder: TrackedTimeListBuilder,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _friendlyDurationPipe: FriendlyDurationPipe,
    private _trackedTimeMasterBuilder: TrackedTimeMasterBuilder,
    trackedTimeDataDispatcher: TrackedTimeDataDispatcher
  ) {
    super(trackedTimeDataDispatcher);
  }

  ngOnInit() {
    this.trackingScreenName = TrackingScreenNamesEnum.tracked_time_list;
    this.trackingObject = this._trackingObjectBuilder.buildInputList(
      TrackingInputTypeValuesEnum.tracked_time
    );
    this.headers = this._trackedTimeListBuilder.buildListHeaders(
      this.trackingObject,
      this.trackingScreenName
    );
    this.yearFilterOptions$ = this._yearFilterOptions
      .asObservable()
      .pipe(filter((yf) => !!yf));
    this.sortByHeader$ = this._sortByHeader
      .asObservable()
      .pipe(filter((x) => !!x));
    this.selectedYearFilter$ = this._selectedYearFilter
      .asObservable()
      .pipe(filter((sy) => !!sy));
    this.lastPageFetched$ = this._lastPageFetched.asObservable();

    this.fetchingList$ = this._trackedTimeDataStore
      .getListFetched(this.filter)
      .pipe(map((fetched: boolean) => !fetched));

    this.fetchingListFilters$ = combineLatest([
      this._trackedTimeDataStore.getListFiltersFetched(this.filter),
      this._trackedTimeDataStore.getListRefreshingFilters(this.filter),
    ]).pipe(
      map(
        ([fetched, refreshingFilter]: [boolean, boolean]) =>
          !fetched || refreshingFilter
      )
    );

    this.groupByFn$ = this.sortByHeader$.pipe(
      map((header: IMasterListHeader) =>
        this._trackedTimeListBuilder.buildGroupByFn(header.sortBy)
      )
    );

    this.filterTotals$ = this.selectedYearFilter$.pipe(
      map((selectedYearFilter: ISelectOption) =>
        this._friendlyDurationPipe.transform(selectedYearFilter.value.duration)
      ),
      startWith('')
    );

    this.groupMetadata$ = combineLatest([
      this.sortByHeader$,
      this.selectedYearFilter$,
      this._trackedTimeDataStore
        .getListFilters(this.filter)
        .pipe(filter((x) => !!x)),
    ]).pipe(
      map(
        ([header, selectedYearFilter, trackedTimeFilters]: [
          IMasterListHeader,
          ISelectOption,
          TrackedTimeFilters
        ]) =>
          this._trackedTimeListBuilder.buildGroupedTotals(
            header,
            trackedTimeFilters.durationPerYearFilters,
            selectedYearFilter.value
          )
      )
    );

    combineLatest([
      this._trackedTimeDataStore
        .getSearchParams(this.filter)
        .pipe(filter((x) => !!x)),
      this._trackedTimeDataStore
        .getListFilters(this.filter)
        .pipe(filter((x) => !!x)),
    ])
      .pipe(takeUntil(this._destroyed))
      .subscribe(
        ([searchParams, trackedTimeFilters]: [
          ISearchParams,
          TrackedTimeFilters
        ]) => {
          this._sortByHeader.next(
            this._trackedTimeListBuilder.buildSortByHeader(searchParams)
          );
          this._lastPageFetched.next(!searchParams.next_page_url);
          this._yearFilterOptions.next(
            this._trackedTimeListBuilder.buildYearFilterOptions(
              trackedTimeFilters.durationPerYearFilters
            )
          );

          this.setSelectedFilter(
            this._yearFilterOptions.value,
            searchParams.year
          );

          if (!this._selectedYearFilter.value) {
            this.filterList(trackedTimeFilters.durationPerYearFilters.first());
          }
        }
      );

    this._trackedTimeDataStore
      .getListFetched(this.filter)
      .pipe(
        takeUntil(this._destroyed),
        take(1),
        filter((fetched: boolean) => !fetched)
      )
      .subscribe(() => this.trackedTimeDataDispatcher.fetchList(this.filter));

    this._trackedTimeDataStore
      .getListFiltersFetched(this.filter)
      .pipe(
        takeUntil(this._destroyed),
        take(1),
        filter((fetched: boolean) => !fetched)
      )
      .subscribe(() =>
        this.trackedTimeDataDispatcher.fetchFilters(this.filter)
      );

    this.noTrackedTimeOptions$ = this._trackedTimeDataStore
      .getDraftTrackedTime()
      .pipe(
        takeUntil(this._destroyed),
        map((trackedTime: TrackedTime) =>
          this._trackedTimeMasterBuilder.buildPrimaryAction(
            this.trackingObject,
            this.trackingScreenName,
            TrackingButtonIdentifierEnum.add_input_empty_state,
            !!trackedTime
          )
        ),
        startWith(
          this._trackedTimeMasterBuilder.buildPrimaryAction(
            this.trackingObject,
            this.trackingScreenName,
            TrackingButtonIdentifierEnum.add_input_empty_state
          )
        ),
        map((primaryAction: IRouter) => ({
          iconName: 'tracked-time-empty-state',
          title: this._translateService.instant(
            'TRACKED_TIME.EMPTY_STATE.TITLE'
          ),
          description: this.noTrackedTimeDescription,
          featureName: Toggles.KEYS.TIME_TRACKING,
          primaryAction,
        }))
      );
  }

  sortList(header: IMasterListHeader) {
    this._sortByHeader.next(header);
    this._lastPageFetched.next(false);

    this.trackedTimeDataDispatcher.sortList(
      header.sortBy,
      header.sortOrder,
      this.filter
    );
  }

  filterList(yearFilter: DurationPerYearFilter) {
    const year = yearFilter.year;

    this.setSelectedFilter(this._yearFilterOptions.value, year);

    this.trackedTimeDataDispatcher.filterList(year, this.filter);
  }

  setSelectedFilter(yearFilterOptions: List<ISelectOption>, year: number) {
    if (yearFilterOptions) {
      const selectedYear = year || ALL_YEARS;

      this._selectedYearFilter.next(
        yearFilterOptions.find(
          (option: ISelectOption) => option.value.year === selectedYear
        )
      );
    }
  }

  fetchNextPage() {
    this.trackedTimeDataDispatcher.fetchNextPage(this.filter);
  }

  trackTime() {
    this.trackedTimeDataDispatcher.newDraftTrackedTime();
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
